import {useEffect, useMemo} from 'react';

import {AlignmentAnalysis, Analyses} from 'analyses/Analyses';
import {DNAAlignmentKind} from 'constants/AlignmentConstants';
import {ALIGNMENT} from 'constants/SequenceAnalysisConstants';
import deepCopy from 'lib/deepCopy';
import {DiffDetails} from 'molbio/biointelligent-links/replace-link/constants';
import useTransientCurrentPolymerModel from 'molbio/hooks/useTransientCurrentPolymerModel';
import DiffOnlySequenceAlignmentTabState from 'molbio/tabstates/DiffOnlySequenceAlignmentTabState';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export const MOCK_ANALYSIS_API_ID = 'seqanl_1';
export const MOCK_ANALYSIS_DB_ID = -1;

export const OPTIONS_TO_HIDE: Array<vo.VisibilityOptionConfig> = [
  vo.TRACE,
  vo.TRACE_QUALITY,
  vo.ALIGNMENT_BASE_QUALITY_CAPITALIZATION,
  vo.VOTES,
  vo.ALIGNMENT_COMMENTS,
];

export default function useTransientSequenceAlignmentTabState(
  referenceSequenceId: string,
  diffDetails: DiffDetails | null
): DiffOnlySequenceAlignmentTabState | null {
  const referenceSequenceModel = useTransientCurrentPolymerModel(referenceSequenceId);
  const sequenceAlignmentTabState = useMemo(() => {
    if (referenceSequenceModel == null) {
      return null;
    }
    const tabState = new DiffOnlySequenceAlignmentTabState({
      sequence: referenceSequenceModel,
      analysisId: MOCK_ANALYSIS_DB_ID,
      alignmentId: MOCK_ANALYSIS_API_ID,
    });
    return tabState;
  }, [referenceSequenceModel]);

  useEffect(() => {
    if (sequenceAlignmentTabState != null && diffDetails != null) {
      const alignmentAnalysis = new AlignmentAnalysis(
        {
          alignmentType: DNAAlignmentKind.TEMPLATE,
          alignment_comments: [],
          // Parsing mutates the inputs, so perform a deep copy first.
          alignments: [
            deepCopy(diffDetails.templateAlignmentRow),
            deepCopy(diffDetails.replacementAlignmentRow),
          ],
          analysis_type: ALIGNMENT,
          apiId: MOCK_ANALYSIS_API_ID,
          attached_file_count: 0,
          attachments: [],
          created_at: '',
          id: MOCK_ANALYSIS_DB_ID,
          name: '',
          sequence_id: referenceSequenceId,
          type: 'analyses',
          versionId: -1,
        },
        {parse: true}
      );
      // Create a new analyses collection so that the Analysis can create an unused URL.
      // eslint-disable-next-line no-new
      new Analyses([alignmentAnalysis], {sequenceID: referenceSequenceId});
      sequenceAlignmentTabState.loadAnalysis(alignmentAnalysis);
    }
  }, [sequenceAlignmentTabState, referenceSequenceId, diffDetails]);

  return sequenceAlignmentTabState;
}
